import {
  BanknoteIcon,
  BedIcon,
  CheckSquareIcon,
  ClipboardListIcon,
  ClocheIcon,
  ClockArrowIcon,
  FileTextIcon,
  GaugeIcon,
  GearIcon,
  GlobeIcon,
  type NavIcon,
  PercentIcon,
  ShieldCheckIcon,
  TicketIcon,
  UserCogIcon,
  UsersIcon,
} from '@/components/layout/navIcons'
import { MODULES, type ModuleKey, type Permission } from '@/constants/permissions'
import { paths } from '@/router/paths'

/**
 * A row inside a module's submenu, revealed while that module's section is the
 * current one.
 *
 * Only User Management uses this: it is a container for two administration
 * screens rather than one listing. Elsewhere a nav row goes straight to its
 * listing, and the "Add New" entry points live in the listing's own header
 * (client decision, 2026-09-07).
 */
export interface NavChild {
  readonly label: string
  readonly to: string
  /** Module whose `view` permission reveals this row. */
  readonly module?: ModuleKey
  /** Extra permission required on top of `module` view. */
  readonly permission?: Permission
  /** Any ONE of these reveals the row — see `NavGate.anyPermission`. */
  readonly anyPermission?: readonly Permission[]
  /** Superadmins only — see `NavGate.superadminOnly`. */
  readonly superadminOnly?: boolean
}

export interface NavItem {
  readonly label: string
  readonly to: string
  /**
   * Module whose `view` permission reveals this item. Optional: a row gated
   * purely by a sensitive permission would otherwise have to borrow an
   * unrelated module's `view` to be visible.
   */
  readonly module?: ModuleKey
  /** Extra permission required on top of module view (e.g. the CMS). */
  readonly permission?: Permission
  /**
   * Any ONE of these reveals the row, for a module where two different grants
   * mean the same access — see `NavGate.anyPermission`.
   */
  readonly anyPermission?: readonly Permission[]
  /** Row icon — the only thing shown when the sidebar is collapsed. */
  readonly icon: NavIcon
  /** Superadmins only — see `NavGate.superadminOnly`. */
  readonly superadminOnly?: boolean
  /**
   * Shown under the row while its section is open. A row that has children is
   * a CONTAINER: it owns no listing, and `Sidebar` shows it whenever any child
   * is visible rather than judging it by its own gate — otherwise a container
   * could appear with an empty submenu, or hide a screen the user may reach.
   */
  readonly children?: readonly NavChild[]
}

/**
 * A titled group of rows, per the OVERVIEW / OPERATIONS / FINANCES /
 * ADMINISTRATION headings. A group whose every item is hidden by permissions renders
 * nothing at all — heading included.
 */
export interface NavGroup {
  readonly label: string
  readonly items: readonly NavItem[]
}

/**
 * Sidebar navigation — rows grouped under section headings, in the approved
 * hierarchy (TURON-192, 2026-10-12): OVERVIEW / OPERATIONS / FINANCES /
 * ADMINISTRATION. Section order and the order of rows inside each section are
 * both part of the acceptance criteria — keep them as listed here.
 *
 * Staff administration (Users, Roles & Permissions) lives under the
 * Administration group's User Management container, per the 2026-09-15 design.
 * Global configuration remains in the topbar's user menu — see
 * `USER_MENU_ITEMS` below. Website Content moved OUT of that menu and into
 * this rail on 2026-09-22, where the design puts it, so the module has one
 * home rather than two.
 *
 * Settings is not in the TURON-192 list; it keeps its existing superadmin-only
 * row at the end of Administration rather than being dropped, since removing a
 * nav row would leave the module reachable only by typing its URL.
 *
 * Every item is permission-gated: a user only ever sees modules their role can
 * view. Hiding is UX; the backend enforces access.
 */
export const NAV_GROUPS: readonly NavGroup[] = [
  {
    label: 'Overview',
    items: [
      { label: 'Dashboard', to: paths.dashboard, module: MODULES.dashboard, icon: GaugeIcon },
    ],
  },
  {
    label: 'Operations',
    items: [
      {
        label: 'Customers',
        to: paths.customers.list,
        module: MODULES.customers,
        icon: UsersIcon,
      },
      { label: 'Events', to: paths.events.list, module: MODULES.events, icon: TicketIcon },
      { label: 'Hotels', to: paths.hotels.list, module: MODULES.hotels, icon: ClocheIcon },
      { label: 'Rooms', to: paths.rooms.list, module: MODULES.rooms, icon: BedIcon },
      {
        label: 'Reservations',
        to: paths.reservations.list,
        module: MODULES.reservations,
        icon: CheckSquareIcon,
      },
      {
        label: 'Waitlist',
        to: paths.waitlist.list,
        module: MODULES.waitlist,
        icon: ClockArrowIcon,
      },
    ],
  },
  {
    label: 'Finances',
    items: [
      { label: 'Reports', to: paths.reports.index, module: MODULES.reports, icon: FileTextIcon },
      {
        label: 'Transactions',
        to: paths.transactions.list,
        module: MODULES.transactions,
        icon: BanknoteIcon,
      },
      {
        label: 'Commission',
        to: paths.commission.list,
        module: MODULES.commission,
        icon: PercentIcon,
      },
    ],
  },
  {
    label: 'Administration',
    items: [
      {
        label: 'Website Content',
        to: paths.cms.list,
        module: MODULES.cms,
        icon: GlobeIcon,
      },
      /*
       * User Management is a container, not a listing: it holds the two staff
       * administration screens and opens a submenu. `/user-management` has no
       * screen of its own and redirects to Users, so the parent row is still
       * clickable and the submenu can tell it is the open section from the
       * shared base path.
       *
       * The two children carry DIFFERENT grants — Users needs `user.view`,
       * Roles needs `role.view` — so the parent deliberately declares
       * none of its own: `Sidebar` shows a container whenever any child is
       * visible, which is the only rule that serves a role holding just one of
       * the two.
       */
      {
        label: 'User Management',
        to: paths.userManagement.index,
        icon: UserCogIcon,
        children: [
          {
            label: 'Users',
            to: paths.userManagement.users,
            module: MODULES.users,
          },
          {
            label: 'Roles & Permissions',
            to: paths.userManagement.roles,
            module: MODULES.roles,
          },
        ],
      },
      /* Under Administration by user instruction (2026-10-29); the design
         draws it under Operations — secure-links/agent.md §23.1. */
      {
        label: 'Secure Hotel Portal',
        to: paths.secureLinks.list,
        module: MODULES.secureLinks,
        icon: ShieldCheckIcon,
      },
      { label: 'Audit', to: paths.audit.trail, module: MODULES.audit, icon: ClipboardListIcon },
      /* Global Configuration — superadmin only (client, 2026-10-01). Also in
         the profile menu; the client kept both entry points. */
      {
        label: 'Settings',
        to: paths.settings.index,
        superadminOnly: true,
        icon: GearIcon,
      },
    ],
  },
]

/**
 * What a nav row is gated by. `NavItem` and `NavChild` each carry these two
 * fields, so one predicate can judge either.
 */
export interface NavGate {
  readonly module?: ModuleKey
  readonly permission?: Permission
  /**
   * Any ONE of these reveals the row. For a module whose backend vocabulary is
   * not settled, where two different grants mean the same access. Combines with `module`/`permission` by AND when both are set.
   */
  readonly anyPermission?: readonly Permission[]
  /**
   * Shown to superadmins only, whatever their role grants — for areas the
   * client reserves for them (Global Configuration, 2026-10-01). A superadmin
   * already passes every permission check, so this only ever hides.
   */
  readonly superadminOnly?: boolean
}

/**
 * The one rule for "may this user see this row": a row may be gated by a
 * module's `view`, by a sensitive permission, or by both.
 *
 * Exported so the sidebar and the landing redirect cannot drift apart — the
 * screen a permission-less user is sent to must be the first row they can
 * actually see in the rail.
 */
export function matchesNavGate(
  gate: NavGate,
  acl: {
    readonly can: (permission: Permission) => boolean
    readonly canViewModule: (module: ModuleKey) => boolean
    readonly isSuperadmin: boolean
  },
): boolean {
  return (
    (!gate.superadminOnly || acl.isSuperadmin) &&
    (!gate.module || acl.canViewModule(gate.module)) &&
    (!gate.permission || acl.can(gate.permission)) &&
    (!gate.anyPermission?.length || gate.anyPermission.some(acl.can))
  )
}

/** A row of the topbar's administration menu. */
export interface UserMenuItem extends NavGate {
  readonly label: string
  readonly to: string
  /** Draws a separator above this item. */
  readonly startsGroup?: boolean
}

/** A nav row reduced to what a redirect needs: where it goes, and its gate. */
export interface NavDestination extends NavGate {
  readonly label: string
  readonly to: string
}

/**
 * Every nav row, flattened out of its group — for consumers that care about the
 * rows themselves and not how they are presented (e.g. `usePageTitle`).
 */
export const NAV_ITEMS: readonly NavItem[] = NAV_GROUPS.flatMap((group) => group.items)

/**
 * Every submenu row, flattened. A child route must be titled by its own label
 * ("Users") rather than by the container it hangs under ("User Management"),
 * so `usePageTitle` needs these alongside `NAV_ITEMS`.
 */
export const NAV_CHILDREN: readonly NavChild[] = NAV_ITEMS.flatMap((item) => item.children ?? [])

/**
 * Every row that OPENS A SCREEN, in the rail's own top-to-bottom order.
 *
 * A container (User Management) is dropped in favour of its children: it owns no
 * screen of its own, and its children carry the grants. Used by
 * `useLandingPath` to answer "where does this user start?" — so the answer is
 * always a screen their role can open, and always the first one they see.
 */
export const NAV_DESTINATIONS: readonly NavDestination[] = NAV_GROUPS.flatMap((group) =>
  group.items.flatMap<NavDestination>((item) =>
    item.children
      ? item.children.map(({ label, to, module, permission, anyPermission, superadminOnly }) => ({
          label,
          to,
          module,
          permission,
          anyPermission,
          superadminOnly,
        }))
      : [
          {
            label: item.label,
            to: item.to,
            module: item.module,
            permission: item.permission,
            anyPermission: item.anyPermission,
            superadminOnly: item.superadminOnly,
          },
        ],
  ),
)

/**
 * The topbar's administration menu, per the design: these admin modules live in
 * the user menu rather than in the sidebar rail.
 *
 * Config, so it sits here with the sidebar's own — `UserMenu` renders it, and
 * `useLandingPath` needs it to answer where a CMS-only or settings-only role
 * starts. Every entry is permission-gated; hiding is UX, the backend enforces.
 */
export const USER_MENU_ITEMS: readonly UserMenuItem[] = [
  { label: 'Global Configuration', to: paths.settings.index, superadminOnly: true },
  { label: 'User Profile', to: paths.users.profile, startsGroup: true },
]

/**
 * Every screen a user could be dropped on after signing in, in the order the
 * portal presents them: the sidebar rail top to bottom, then the administration
 * menu.
 *
 * The menu's ungated row (User Profile) is deliberately not a candidate. It
 * belongs to the signed-in user rather than to a grant, so it would match every
 * role and make the fallback meaningless — the point of the
 * list is to find a screen the role was actually given.
 */
export const LANDING_DESTINATIONS: readonly NavDestination[] = [
  ...NAV_DESTINATIONS,
  ...USER_MENU_ITEMS.filter(
    (item) => item.module || item.permission || item.anyPermission?.length || item.superadminOnly,
  ).map(({ label, to, module, permission, anyPermission, superadminOnly }) => ({
    label,
    to,
    module,
    permission,
    anyPermission,
    superadminOnly,
  })),
]
